import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DEAL_LOST_REASONS, type CrmDeal, type CrmDealDetail, type DealStage } from '@aviaconsole/shared';
import { capi } from '../../api/client';
import { useI18n } from '../../i18n';
import { useToast } from '../../components/Toast';
import { Button, Input, Modal, Select, Textarea } from '../../components/ui';
import { crmLabel, crmOptions, localToIso } from '../../lib/crm';

/** Stages that ask for something before the move: a contract date, a reason, a demo date or a proposal link. */
const ASKS: Partial<Record<DealStage, true>> = { won: true, lost: true, demo_scheduled: true, proposal_sent: true };

/**
 * Moving a deal, from the board or the account page. Stages that need input
 * open a small dialog; the rest move at once. The server decides what is
 * allowed (forward only, never to paying) and its refusal is shown as is.
 */
export function useMoveDeal(): { move: (deal: Pick<CrmDeal, 'id' | 'title'>, to: DealStage) => void; dialog: ReactNode; pending: boolean } {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const [ask, setAsk] = useState<{ deal: Pick<CrmDeal, 'id' | 'title'>; to: DealStage } | null>(null);
  const [form, setForm] = useState({ wonAt: new Date().toISOString().slice(0, 10), lostReason: 'no_response', lostNote: '', demoAt: '', proposalUrl: '', note: '' });

  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => capi<CrmDealDetail>(`/crm/deals/${id}/stage`, { method: 'POST', json: body }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['crm-deals'] });
      qc.invalidateQueries({ queryKey: ['crm-account', d.accountId] });
      qc.invalidateQueries({ queryKey: ['crm-funnel'] });
      setAsk(null);
      toast.push(`${d.title}: ${crmLabel('stage', d.stage, lang)}`);
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const move = (deal: Pick<CrmDeal, 'id' | 'title'>, to: DealStage) => {
    if (ASKS[to]) setAsk({ deal, to });
    else mutation.mutate({ id: deal.id, body: { stage: to } });
  };

  const submit = () => {
    if (!ask) return;
    const body: Record<string, unknown> = { stage: ask.to, note: form.note || undefined };
    if (ask.to === 'won') body.wonAt = form.wonAt;
    if (ask.to === 'lost') Object.assign(body, { lostReason: form.lostReason, lostNote: form.lostNote || undefined });
    if (ask.to === 'demo_scheduled') body.demoAt = localToIso(form.demoAt);
    if (ask.to === 'proposal_sent' && form.proposalUrl) body.proposalUrl = form.proposalUrl;
    mutation.mutate({ id: ask.deal.id, body });
  };

  const title = ask && ({ won: t('wonTitle'), lost: t('lostTitle'), demo_scheduled: t('demoTitle'), proposal_sent: t('proposalTitle') } as Partial<Record<DealStage, string>>)[ask.to];
  const dialog = ask && (
    <Modal title={`${title} · ${ask.deal.title}`} onClose={() => setAsk(null)}>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {ask.to === 'won' && <Input type="date" required label={t('fContractDate')} value={form.wonAt} onChange={(e) => setForm({ ...form, wonAt: e.target.value })} />}
        {ask.to === 'lost' && (
          <>
            <Select label={t('fLostReason')} options={crmOptions('lostReason', DEAL_LOST_REASONS, lang)} value={form.lostReason} onChange={(e) => setForm({ ...form, lostReason: e.target.value })} />
            <Input label={t('fLostNote')} value={form.lostNote} onChange={(e) => setForm({ ...form, lostNote: e.target.value })} />
          </>
        )}
        {ask.to === 'demo_scheduled' && <Input type="datetime-local" label={t('fDemoAt')} value={form.demoAt} onChange={(e) => setForm({ ...form, demoAt: e.target.value })} />}
        {ask.to === 'proposal_sent' && <Input type="url" label={t('fProposalUrl')} value={form.proposalUrl} onChange={(e) => setForm({ ...form, proposalUrl: e.target.value })} />}
        <Textarea label={t('fBody')} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={() => setAsk(null)}>{t('cancel')}</Button>
          <Button variant={ask.to === 'lost' ? 'danger' : 'primary'} type="submit" disabled={mutation.isPending}>{crmLabel('stage', ask.to, lang)}</Button>
        </div>
      </form>
    </Modal>
  );
  return { move, dialog, pending: mutation.isPending };
}
